import { useState } from "react";
import { Menu, ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import logo from "@/assets/TGF_logo.png";

const TopBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const serviceLinks = [
    { href: "/services/websites", label: "בניית אתרים" },
    { href: "/services/ecommerce", label: "חנויות איקומרס" },
    { href: "/services/shopify", label: "אינטגרציות שופיפיי" },
    { href: "/services/custom-interfaces", label: "ממשקים מותאמים" },
    { href: "/services/automations", label: "אוטומציות" },
  ];

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
    } else if (href.startsWith("#")) {
      if (isHomePage) {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        navigate("/", { state: { scrollTo: href } });
      }
    }
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-border/30">
      <div className="container mx-auto px-4 py-2 sm:py-3">
        {/* Mobile Layout */}
        <div className="flex md:hidden items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>

          <button onClick={handleLogoClick} className="flex-shrink-0">
            <img
              src={logo}
              alt="Top Gear Funnels Logo"
              width={75}
              height={75}
              className="h-[62px] sm:h-[75px] w-auto animate-fade-in transition-transform duration-300 hover:scale-110"
            />
          </button>

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="p-2 hover:bg-accent/50 rounded-lg transition-colors"
                aria-label="תפריט ניווט"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px]">
              <div className="flex flex-col gap-4 mt-8">
                <div className="flex items-center justify-between mb-4 pb-4 border-b">
                  <img src={logo} alt="Top Gear Funnels" className="h-[60px] w-auto" />
                </div>

                <nav className="flex flex-col gap-2">
                  <button
                    onClick={() => handleNavClick("/")}
                    className="px-4 py-3 text-lg font-medium text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-lg transition-all duration-200 text-right"
                  >
                    דף הבית
                  </button>

                  <div className="px-4 py-2 text-sm font-semibold text-muted-foreground">שירותים</div>
                  {serviceLinks.map((link) => (
                    <button
                      key={link.href}
                      onClick={() => handleNavClick(link.href)}
                      className="px-6 py-3 text-base font-medium text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-lg transition-all duration-200 text-right"
                    >
                      {link.label}
                    </button>
                  ))}

                  <button
                    onClick={() => handleNavClick("#about")}
                    className="px-4 py-3 text-lg font-medium text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-lg transition-all duration-200 text-right"
                  >
                    אודות
                  </button>
                  <button
                    onClick={() => handleNavClick("#contact-form")}
                    className="px-4 py-3 text-lg font-medium text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-lg transition-all duration-200 text-right"
                  >
                    צרו קשר
                  </button>
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-3 items-center gap-4">
          {/* Right: Theme Toggle */}
          <div className="flex justify-start">
            <ThemeToggle />
          </div>

          {/* Center: Navigation */}
          <nav className="flex flex-nowrap items-center justify-center gap-0.5 lg:gap-2">
            <button
              onClick={() => handleNavClick("/")}
              className="px-2 lg:px-4 py-2 text-xs lg:text-base font-medium text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              ראשי
            </button>

            {/* Services Dropdown */}
            <div className="relative group">
              <button
                onClick={() => handleNavClick("#services")}
                className="px-2 lg:px-4 py-2 text-xs lg:text-base font-medium text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-lg transition-all duration-200 whitespace-nowrap flex items-center gap-1"
              >
                שירותים
                <ChevronDown className="w-3 h-3 lg:w-4 lg:h-4" />
              </button>
              <div className="absolute top-full right-0 mt-1 w-52 glass-panel rounded-xl border border-border/30 shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  {serviceLinks.map((link) => (
                    <button
                      key={link.href}
                      onClick={() => handleNavClick(link.href)}
                      className="block w-full text-right px-4 py-2.5 text-sm text-foreground/80 hover:text-foreground hover:bg-accent/50 transition-colors"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={() => handleNavClick("#about")}
              className="px-2 lg:px-4 py-2 text-xs lg:text-base font-medium text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              אודות
            </button>
            <button
              onClick={() => handleNavClick("#contact-form")}
              className="px-2 lg:px-4 py-2 text-xs lg:text-base font-medium text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              צרו קשר
            </button>
          </nav>

          {/* Left: Logo */}
          <div className="flex justify-end">
            <button onClick={handleLogoClick} className="flex-shrink-0">
              <img
                src={logo}
                alt="Top Gear Funnels Logo"
                width={88}
                height={88}
                className="h-[75px] lg:h-[88px] w-auto animate-fade-in transition-transform duration-300 hover:scale-110"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
